import { Injectable } from '@angular/core';
import { BaseFacilityService } from './base-facility.service';
import { CommunicationService } from '../../../services/communication.service';
import { UrlSegments } from '../../jurisdiction.constants';
import {
  BaseDataObject,
  MeterDataObject,
  OldMeterData,
  WaterDeviceData,
  WaterDeviceObject,
  WaterFacilityData,
  WaterFacilityResponse,
  WaterMeterData,
} from '../../facilities-form/facilities-form.domain';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CommonService } from '../../../services/common.service';
import { EnvironmentService } from '../../../services/environment.service';
import { InstallationFlowType } from '../../../constants/app.constants';
import { LocalStorageService } from '../../../services/local-storage.service';

@Injectable({
  providedIn: 'root',
})
export class WaterFacilityService extends BaseFacilityService {
  private currentFlowType: string;

  constructor(
    communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private commonService: CommonService,
    private localStorageService: LocalStorageService
  ) {
    super(communicationService, {
      UpdateInstallationUrl: UrlSegments.UpdateWaterInstallation,
      UpdateFacilityUrl: UrlSegments.UpdateWaterFacility,
      UpdateDeviceUrl: UrlSegments.UpdateWaterDevice,
      CreateDeviceUrl: UrlSegments.CreateWaterDevice,
      UpdateMeterUrl: UrlSegments.UpdateWaterMeter,
      CreateMeterUrl: UrlSegments.CreateWaterMeter,
      UpdateOldMeterUrl: UrlSegments.UpdateOldWaterMeter,
      CreateOldMeterUrl: UrlSegments.CreateOldWaterMeter,
    });

    this.currentFlowType = this.localStorageService.getItem('installation_flow');
  }

  protected createFacility(request: any): Observable<string> {
    return this.communicationService
      .postData<WaterFacilityResponse>(UrlSegments.CreateWaterFacility, request)
      .pipe(map(response => response.waterfacility.find(() => true).facility_id));
  }

  protected mapToFacilityRequest(facility: BaseDataObject): WaterFacilityData {
    const result: WaterFacilityData = {
      peakflowrate: this.commonService.parseLocaleFractionsToStandard(facility.peakFlowRate),
      sk_device_location: facility.placeOfMeter,
      sk_water_facility_type: facility.facilityType,
      sumflowrate: this.commonService.parseLocaleFractionsToStandard(facility.sumFlowRate),
      transaction_no: facility.noOfConnections,
      commercial_sector: facility.sector,
      other_location_details: facility.otherLocation,
      ...(this.environmentService.showMeasuringPointCheckbox && {
        sensitive_measuring_point: facility.measuringPointCheckbox,
      }),
      other_type_detail: facility.otherType,
      reason_for_request: facility.reasonForRequest,
      heated_living_space: facility.heatedLivingSpace,
      volume_flow_qmax: facility.volumeFlowQmax,
      flow_temperature: facility.flowTemperature,
      return_temperature: facility.returnTemperature,
    };

    if (this.environmentService.showWaterSystemComponents) {
      result.pipe_material = facility.waterPipeMaterial;
      result.installation_type = facility.waterInstallationType;
      result.reflux_fuse = facility.waterRefluxFuse;
      result.devices = facility.waterDevices;
    }

    return result;
  }

  protected mapToFacilityDeviceRequest(device: WaterDeviceObject): WaterDeviceData {
    const data = {
      ...device,
      ...device.customFields,
    };

    return {
      sk_tappingpoint_type: data.specialApplianceDropDown,
      consumer_count: data.amount,
      vrhot: data.vrHot ?? '',
      vrcold: data.vrCold ?? '',
      vr_total_volume: data.vrTotalVolume ?? '',
      ...(data.swknManufacturer && { swkn_manufacturer: data.swknManufacturer }),
      ...(data.swknDWGVCertifiedCheckbox != null &&
        data.swknDWGVCertifiedCheckbox != undefined && { swkn_dwgv_certified: data.swknDWGVCertifiedCheckbox }),
      ...(data.swknType && { swkn_type: data.swknType }),
      ...(data.swknFlowRate && { swkn_flow_rate: data.swknFlowRate }),
      ...(data.swknDeliveryPressure && { swkn_delivery_pressure: data.swknDeliveryPressure }),
      ...(data.swknReqExtinguishingVolume && { swkn_req_extinguishing_volume: data.swknReqExtinguishingVolume }),
      ...(data.swknWaterFacilitySystemType && { sk_swkn_water_facility_system_type: data.swknWaterFacilitySystemType }),
    };
  }

  protected getDevices(facility: BaseDataObject): any[] {
    return facility.waterDevice || [];
  }

  protected mapToMeterRequest(data: MeterDataObject): WaterMeterData {
    const dataObject: WaterMeterData = {
      consumption_forecast: data.annualConsumptionForecast,
      meter_count: data.amount,
      sk_communication: data.selectComm,
      sk_meter_mount: data.selectMount,
      sk_water_meter_type: data.meterType,
      sk_water_meter_size: data.counterSize,
      other_size_details: data.otherType,
      sk_mounting_alignment: data.alignment,
      sk_standard_compliance: data.meterCompliance,
      remote_readout_option: data.remoteReadoutOption,
      location: data.location,
      ...(this.environmentService.showOldMeterField && { old_meter_number: data.oldMeterNumber }),
    };

    if (
      !this.environmentService.facilityWaterHiddenFields.swknRemoteReadoutCheckbox &&
      this.currentFlowType === InstallationFlowType.NewInstallation
    ) {
      dataObject.remote_readout = data.swknRemoteReadoutCheckbox ? '1' : '0';
    }

    return dataObject;
  }

  protected mapToOldMeterRequest(data: MeterDataObject): OldMeterData {
    return {
      replaced_meter_no: data.meterNumber,
      replaced_meter_reading: data.meterReading,
      replaced_meter_image_id: data.meterImageId,
      replaced_meter_action: data.meterAction,
    };
  }
}
